import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { GlassCard, PageLoader, StatusBadge } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { LifeBuoy, Plus, ArrowLeft, Send } from "lucide-react";

const CATEGORIES = ["Backup Failure", "Restore Assistance", "Connectivity", "Billing", "Storage", "Account", "Other"];

export default function Tickets() {
  const [tickets, setTickets] = useState(null);
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({ subject: "", category: "Backup Failure", priority: "normal", message: "" });
  const [reply, setReply] = useState("");

  const load = () => api.get("/tickets").then((r) => setTickets(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/tickets", form); toast.success("Ticket created"); setForm({ subject: "", category: "Backup Failure", priority: "normal", message: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const openTicket = (id) => api.get(`/tickets/${id}`).then((r) => setOpen(r.data));
  const sendReply = async () => {
    try { await api.post(`/tickets/${open.ticket.id}/reply`, { message: reply }); setReply(""); openTicket(open.ticket.id); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!tickets) return <PageLoader />;

  if (open) {
    return (
      <div className="mx-auto max-w-2xl">
        <button onClick={() => { setOpen(null); load(); }} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-400 hover:text-cyan-300"><ArrowLeft className="h-4 w-4" /> Back to tickets</button>
        <GlassCard className="p-6">
          <div className="flex items-center justify-between"><h1 className="font-display text-xl font-bold text-slate-50">{open.ticket.subject}</h1><StatusBadge status={open.ticket.status === "open" ? "active" : "none"} label={open.ticket.status} /></div>
          <div className="mt-1 text-xs text-slate-500">{open.ticket.category} · {open.ticket.priority}</div>
          <div className="mt-5 space-y-3">
            {open.messages.map((m) => (
              <div key={m.id} className={`rounded-lg p-3 text-sm ${m.is_admin ? "border border-cyan-500/20 bg-cyan-500/5" : "border border-slate-800 bg-[#06080D]"}`}>
                <div className="mb-1 text-xs text-slate-500">{m.is_admin ? "ZettaKeep Support" : m.author} · {new Date(m.created_at).toLocaleString()}</div>
                <p className="text-slate-200">{m.message}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 flex gap-2">
            <Input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type a reply…" data-testid="ticket-reply-input" className="bg-slate-950 border-slate-700" />
            <button onClick={sendReply} disabled={!reply} data-testid="ticket-reply-send" className="rounded-lg bg-cyan-400 px-4 text-[#06080D] disabled:opacity-50"><Send className="h-4 w-4" /></button>
          </div>
        </GlassCard>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2"><LifeBuoy className="h-5 w-5 text-cyan-400" /><h1 className="font-display text-2xl font-bold text-slate-50">Support</h1></div>
        <Dialog>
          <DialogTrigger asChild><button data-testid="new-ticket-btn" className="inline-flex items-center gap-2 rounded-lg bg-cyan-400 px-4 py-2 text-sm font-semibold text-[#06080D] hover:bg-cyan-300"><Plus className="h-4 w-4" /> New ticket</button></DialogTrigger>
          <DialogContent className="bg-[#0D111A] border-slate-800">
            <DialogHeader><DialogTitle className="text-slate-100">Open a support ticket</DialogTitle></DialogHeader>
            <Input placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="ticket-subject" className="bg-slate-950 border-slate-700" />
            <div className="grid grid-cols-2 gap-3">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="ticket-category" className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200">{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200"><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option></select>
            </div>
            <Textarea placeholder="Describe your issue…" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="ticket-message" className="bg-slate-950 border-slate-700" />
            <button onClick={create} disabled={!form.subject || !form.message} data-testid="ticket-submit" className="w-full rounded-lg bg-cyan-400 py-2.5 font-semibold text-[#06080D] disabled:opacity-50">Create ticket</button>
          </DialogContent>
        </Dialog>
      </div>

      {tickets.length === 0 ? (
        <GlassCard className="p-8 text-center text-slate-400">No support tickets yet.</GlassCard>
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => (
            <button key={t.id} onClick={() => openTicket(t.id)} data-testid={`ticket-${t.id}`} className="block w-full text-left">
              <GlassCard className="flex items-center justify-between p-4 hover:border-cyan-500/40">
                <div><div className="font-semibold text-slate-100">{t.subject}</div><div className="text-xs text-slate-500">{t.category} · {new Date(t.updated_at).toLocaleString()}</div></div>
                <StatusBadge status={t.status === "open" ? "active" : "none"} label={t.status} />
              </GlassCard>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
